// 글로리샵 분석 화면의 표/카드 파트. 상태·페치는 GloryShopAnalyticsPage 가 담당하고
// 여기서는 집계 결과(GloryAgg) 를 그리기만 한다. ShopRevenueTables.tsx 의 카드 구조를 따른다.

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { GloryAgg } from '@rb/shared/analytics/glory-agg';

const n = (v: number): string => v.toLocaleString('en-US');

export function GloryKpiRow({ agg }: { agg: GloryAgg }) {
  const cells: { label: string; value: string }[] = [
    { label: '총 소모 글로리', value: n(agg.kpi.totalGlory) },
    { label: '구매 건수', value: n(agg.kpi.purchases) },
    { label: '구매자 수', value: n(agg.kpi.buyers) },
    { label: '인당 평균', value: n(agg.kpi.avgPerBuyer) },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {cells.map((c) => (
        <Card key={c.label}>
          <CardHeader className="pb-1">
            <CardTitle className="text-xs text-muted-foreground">{c.label}</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold tabular-nums">{c.value}</CardContent>
        </Card>
      ))}
    </div>
  );
}

export function GloryDailyCard({ agg }: { agg: GloryAgg }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>일별 추이</CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th>날짜</th>
              <th className="text-right">소모 글로리</th>
              <th className="text-right">건수</th>
            </tr>
          </thead>
          <tbody>
            {agg.daily.map((d) => (
              <tr key={d.date}>
                <td>{d.date}</td>
                <td className="text-right tabular-nums">{n(d.glory)}</td>
                <td className="text-right tabular-nums">{n(d.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export function GloryItemCard({ agg }: { agg: GloryAgg }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>상품별 판매</CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th>상품</th>
              <th className="text-right">건수</th>
              <th className="text-right">회차</th>
              <th className="text-right">소모 글로리</th>
              <th className="text-right">구매자</th>
            </tr>
          </thead>
          <tbody>
            {agg.byItem.map((r) => (
              <tr key={r.itemId}>
                <td className="font-mono text-xs">{r.itemId}</td>
                <td className="text-right tabular-nums">{n(r.count)}</td>
                <td className="text-right tabular-nums">{n(r.pulls)}</td>
                <td className="text-right tabular-nums">{n(r.glory)}</td>
                <td className="text-right tabular-nums">{n(r.buyers)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export function GloryTopSpendersCard({
  agg,
  nicknameOf,
}: {
  agg: GloryAgg;
  nicknameOf: (account: string) => string | undefined;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>유저별 상위 소비 (상위 50명)</CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th>계정</th>
              <th className="text-right">소모 글로리</th>
              <th className="text-right">건수</th>
            </tr>
          </thead>
          <tbody>
            {agg.byAccount.slice(0, 50).map((r) => (
              <tr key={r.account}>
                <td className="font-mono text-xs">{nicknameOf(r.account) ?? r.account}</td>
                <td className="text-right tabular-nums">{n(r.glory)}</td>
                <td className="text-right tabular-nums">{n(r.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
